import type {
  Gallery,
  GalleryWatermark,
  UpdateGalleryWatermarkInput,
  WatermarkPosition,
  WatermarkStrength,
} from "./gallery.api";

// The Watermark editor's rules (.scratch/gallery-look/spec.md, decision 12). Mirror
// GALLERY_WATERMARK_LOGO_MAX_BYTES and GALLERY_WATERMARK_TEXT_MAX_LENGTH in
// apps/api/src/modules/gallery/watermark/.
export const WATERMARK_LOGO_MAX_BYTES = 5 * 1024 * 1024;
export const WATERMARK_TEXT_MAX_LENGTH = 80;

export type WatermarkLogoRefusal = "notPng" | "tooLarge";

export function checkWatermarkLogoFile(
  file: Pick<File, "name" | "type" | "size">
): WatermarkLogoRefusal | null {
  const type = file.type.trim().toLowerCase();
  if (type ? type !== "image/png" : !/\.png$/i.test(file.name)) {
    return "notPng";
  }
  return file.size > WATERMARK_LOGO_MAX_BYTES ? "tooLarge" : null;
}

type ProfileName = { firstName?: string | null; lastName?: string | null; title?: string | null };

// What the API draws without the template's own text: first and last name, else the Profile title.
export function watermarkProfileName(profile: ProfileName | null | undefined): string {
  const name = [profile?.firstName, profile?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return name || profile?.title?.trim() || "";
}

// The editor's form; the text as typed ('' for the Profile name).
export interface WatermarkForm {
  kind: GalleryWatermark["kind"];
  text: string;
  position: WatermarkPosition;
  strength: WatermarkStrength;
}

export function watermarkForm(template: GalleryWatermark): WatermarkForm {
  return {
    kind: template.kind,
    text: template.text ?? "",
    position: template.position,
    strength: template.strength,
  };
}

// Only what changed goes to `updateGalleryWatermark`. LOGO without an uploaded logo is a choice on
// screen only (the upload saves it).
export function buildWatermarkInput(
  template: GalleryWatermark,
  form: WatermarkForm
): UpdateGalleryWatermarkInput {
  const input: UpdateGalleryWatermarkInput = {};
  const text = form.text.trim() || null;
  if (text !== (template.text ?? null)) {
    input.text = text;
  }
  if (form.kind !== template.kind && (form.kind === "TEXT" || template.hasLogo)) {
    input.kind = form.kind;
  }
  if (form.position !== template.position) {
    input.position = form.position;
  }
  if (form.strength !== template.strength) {
    input.strength = form.strength;
  }
  return input;
}

const PREVIEW_OPACITY: Record<WatermarkStrength, number> = {
  LIGHT: 0.3,
  MEDIUM: 0.55,
  STRONG: 0.8,
};

// The preview's opacity per strength; the API picks its own for the real copies.
export function watermarkPreviewOpacity(strength: WatermarkStrength): number {
  return PREVIEW_OPACITY[strength];
}

// Design view hint: the Watermark is on while guests may download originals, which carry none.
export function showsOriginalsWithoutWatermarkHint(
  gallery: Pick<Gallery, "downloads"> & { watermark?: boolean }
) {
  return gallery.watermark === true && (gallery.downloads === "ALL" || gallery.downloads === "ORIGINAL");
}

// Without the Plan the kind cannot switch, so a logo kept while the mark is text would be out of
// reach; the editor still shows it with "Remove logo", which frees its Storage on every Plan.
export function showsStoredLogoOnly(
  template: Pick<GalleryWatermark, "canChange" | "hasLogo">,
  kind: GalleryWatermark["kind"]
): boolean {
  return !template.canChange && template.hasLogo && kind === "TEXT";
}
